import { CalculatorApiClient } from 'api'
import { ApplicationContext } from 'pages/application/state'
import { AgreementCode, ApplicationType } from 'shared'
import { DPFormField, DPInputCheckbox, DPInputDropdown, DPInputNumber, DPInputTextReadOnly, PageSectionHeader } from '@framework'
import { useApiCall, useApiClient, useSession, useTranslation } from '@hooks'
import React, { useContext, useEffect, useState } from 'react'
import styles from './LoanCalculationComponent.module.css'
import { formatNumber } from 'shared/utils'
import { Agreement } from 'model'
import { IApplicationContext } from 'pages/application/model'
import { ISession } from '@session'
import { isCorporateApplicant, isObjectYoungerThan8Years } from 'shared/ApplicationHelper'
import { Icon } from 'semantic-ui-react'

type FieldName =
  'objectPrice' |
  'downPayment' |
  'paymentPerMonth' |
  'paymentTerms' |
  'interestType' |
  'interestRate' |
  'documentFee' |
  'commitmentFee'

function getFields(agreementCode: number): FieldName[] {
  switch (agreementCode) {
    case AgreementCode.PurchaseContract100:
      return ['objectPrice', 'downPayment', 'paymentPerMonth', 'paymentTerms', 'interestType', 'interestRate', 'commitmentFee']
    case AgreementCode.PurchaseContractWithoutSalesCommission:
    case AgreementCode.PurchaseContractMotorcycleWithoutSalesCommission:
    case AgreementCode.PurchaseContractCaravanWithoutSalesCommission:
      return ['objectPrice', 'downPayment', 'paymentPerMonth', 'paymentTerms', 'interestType', 'interestRate']
    case AgreementCode.PersonnelLoan:
    case AgreementCode.PersonnelLoanMotorcycle:
    case AgreementCode.PersonnelLoanCamping:
      return ['objectPrice', 'paymentTerms', 'interestType', 'interestRate']
    default:
      return ['objectPrice', 'downPayment', 'paymentPerMonth', 'paymentTerms', 'interestType', 'interestRate', 'documentFee', 'commitmentFee']
  }
}

type FieldDefinitions = {
  [key in FieldName]: () => JSX.Element
}

interface Props {
  applicationType: ApplicationType,
  agreements: Agreement[]
}

export function LoanCalculationComponent(props: Props) {
  const $t = useTranslation('LoanCalculationComponent')
  const session = useSession()
  const context = useContext(ApplicationContext)
  const dealerId = session.currentDealer?.id;
  const agreementCode = context.application.calculation.agreementCode
  const agreement = props.agreements.find(x => x.code === agreementCode)
  const fields = getFields(context.application.calculation.agreementCode)
  const calculatorApiClient = useApiClient(a => new CalculatorApiClient(a))
  const [interestTypes, , getInterestTypes] = useApiCall(calculatorApiClient.getInterestTypes, [])
  const interestRateDisabled = props.applicationType === ApplicationType.Personnel;
  const dealerGapValue = context.application.calculation.gapValue;
  const showGapFormField = ShouldShowGapFormField(context, session, agreement);
  const [feesHidden, setFeesHidden] = useState(false)
  const hasFeeFields = fields.includes('documentFee') || fields.includes('commitmentFee');

  useEffect(() => {
    getInterestTypes(dealerId!, agreementCode)
    if (!hasFeeFields) {
      setFeesHidden(false);
    }
  }, [dealerId, agreementCode])

  const fieldDefinitions: FieldDefinitions = {

    'objectPrice': () =>
      <DPFormField
        key="objectPrice"
        label={$t('Sales price including lev.')}
        name="objectPrice"
      >
        <DPInputNumber
          name="objectPrice"
          dataQa="calculation_object_price"
          label="kr"
        />
      </DPFormField>,

    'downPayment': () =>
      <DPFormField
        key="downPayment"
        name="downPayment"
        label={$t('Down payment')}
      >
        <DownPaymentInput />
      </DPFormField>,

    'paymentPerMonth': () =>
      <DPFormField
        key="paymentPerMonth"
        label={$t('Payment per month')}
        name="paymentPerMonth"
      >
        <DPInputNumber
          name="paymentPerMonth"
          precision={2}
          dataQa="calculation_payment_per_month"
          label="kr"
          disabled={context.isEditMode}
        />
      </DPFormField>,

    'paymentTerms': () =>
      <DPFormField
        key="paymentTerms"
        label={$t('Number of months')}
        name="paymentTerms"
      >
        <DPInputNumber
          name="paymentTerms"
          dataQa="calculation_payment_terms"
        />
      </DPFormField>,

    'interestType': () =>
      <DPFormField
        key="interestType"
        label={$t('Interests type')}
        name="interestType"
      >
        <DPInputDropdown
          name="interestType"
          options={interestTypes}
          disabled={interestTypes.length < 2 || context.isEditMode}
          dataQa="calculation_interest_type"
        />
      </DPFormField>,

    'interestRate': () =>
      <DPFormField
        key="interestRate"
        label={$t('Interest rate')}
        name="interestRate"
      >
        <DPInputNumber
          name="interestRate"
          precision={2}
          dataQa="calculation_interest_rate"
          label="%"
          disabled={interestRateDisabled || context.isEditMode}
        />
      </DPFormField>,

    'documentFee': () =>
      <div key="documentFee" className={feesHidden ? styles['fee-collapsed'] : undefined}>
        <DPFormField
          label={$t('Document fee')}
          name="documentFee"
        >
          <DPInputNumber
            name="documentFee"
            dataQa="calculation_document_fee"
            label="kr"
            disabled={context.isEditMode}
          />
        </DPFormField>
      </div>,

    'commitmentFee': () =>
      <div key="commitmentFee" className={feesHidden ? styles['fee-collapsed'] : undefined}>
        <DPFormField
          label={$t('Commission fee')}
          name="commitmentFee"
        >
          <DPInputNumber
            name="commitmentFee"
            dataQa="calculation_commitment_fee"
            label="kr"
            disabled={context.isEditMode}
          />
        </DPFormField>
      </div>,
  }

  const fieldComponents =
    fields
      .map(field => fieldDefinitions[field]())

  if (showGapFormField) {
    fieldComponents.push(GapFormField($t, dealerGapValue, context));
  }

  return <>
    <PageSectionHeader>
      {$t('Loan calculation')}
    </PageSectionHeader>
    {fieldComponents}
    {hasFeeFields && (
      <div className={styles['fee-toggle-row']}>
        <button
          type="button"
          className={styles['fee-toggle-btn']}
          onClick={() => setFeesHidden(v => !v)}
          data-qa="toggle_fees_visibility"
        >
          <Icon name={feesHidden ? 'eye slash' : 'eye'} />
        </button>
      </div>
    )}
  </>
}

function DownPaymentInput() {
  return (
    <div className={styles['downpayment-input']}>

      <div className={styles['downpayment-value']}>
        <DPInputNumber
          name="downPayment"
          label="kr"
          dataQa="calculation_down_payment_amount"
        />
      </div>

      <div className={styles['downpayment-pct']}>
        <DPInputNumber
          name="downPaymentPct"
          precision={2}
          label="%"
          dataQa="calculation_down_payment_percent"
        />
      </div>
    </div>
  )
}

function GapFormField($t: any, dealerGapValue: number, context: IApplicationContext) {
  return (
    <DPFormField
      key="gap"
      label={$t('Gap')}
      name="gap"
    >
      <div className={styles['gap-input']}>
        <div className={styles['gap-value']}>
          <DPInputTextReadOnly
            value={formatNumber(dealerGapValue)}
            label="kr"
            dataQa="calculation_dealer_gap_value"
          />
        </div>

        <div className={styles['gap-enabled']}>
          <DPInputCheckbox 
            name="dealerGapEnabled"
            disabled={context.isEditMode}
          />
        </div>
      </div>
    </DPFormField>
  )
}

function ShouldShowGapFormField(context: IApplicationContext, session: ISession, agreement: Agreement | undefined) : boolean {
  return context.application.type !== ApplicationType.Personnel && !isCorporateApplicant(context.application.applicantType) && !!session.currentDealer?.gapEnabled
    && session.currentUser.gapEnabled && !!agreement?.isGapEnabled && isObjectYoungerThan8Years(context.application.calculation?.objectAgeMonths);
}
